"use client";

import { useState, type ReactNode } from "react";
import { LeadForm } from "@/components/marketing/lead-form/lead-form";
import { HeroPreview } from "@/components/marketing/sections/hero-preview";

/*
 * Hero interactive — the client coordination + layout layer.
 *
 * Couples the (client) lead form and the (client) assembling preview: the
 * form's `onUrlSubmit` callback flips `assembled`, which the preview consumes
 * to run its self-assemble sequence.
 *
 * `children` is the static left-column copy (kicker, H1, subhead) rendered by
 * the SERVER `Hero` component, so the H1 LCP element ships as real
 * server-rendered markup; only the form + preview are client-owned here.
 *
 * Reduced motion is honored downstream: the preview's assemble blocks carry
 * `mkt-assemble-item`, which globals.css suppresses under
 * prefers-reduced-motion, so on submit the assembled state simply appears.
 */
export function HeroInteractive({ children }: { children: ReactNode }) {
  const [assembled, setAssembled] = useState(false);

  return (
    <div className="relative mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 sm:px-8 md:py-28 lg:grid-cols-2 lg:gap-16">
      {/* ── LEFT (dark): the pitch + the form ─────────────────────────────── */}
      <div className="flex flex-col">
        {children}

        <div className="mt-9 max-w-md">
          <LeadForm placement="hero" onUrlSubmit={() => setAssembled(true)} />
          <p className="mt-3 font-mono text-[11px] tracking-wide text-(--color-foreground-faint)">
            No credit card · See your redesign in seconds
          </p>
        </div>
      </div>

      {/* ── RIGHT (light): the self-assembling before → after preview ──────── */}
      <div className="relative lg:pl-4">
        <HeroPreview assembled={assembled} />
      </div>
    </div>
  );
}
